import { useEffect, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Control, useForm, useWatch } from 'react-hook-form';
import { BsSortDown, BsSortUp } from 'react-icons/bs';
import { ControlProps, components } from 'react-select';
import { TIME_FORMAT_OUTPUT } from '../../constants/time';
import { AutoSuggest } from '../AutoSuggest';
import { DateRangePiker } from '../DateRangePiker';
import { FilterDropdown } from '../FilterDropDown';
import { SelectController } from '../SelectController';
import {
  SPECIALTIES_OPTIONS,
  defaultFilter,
  PROVIDER_SORT_OPTIONS,
  HCO_SORT_OPTIONS,
  SortBy,
} from './constants';
import { BookingRequestsFiltersState } from './models';

export const BookingRequestsFilters = ({
  filters,
  onApply,
  userIsProvider,
  searchOptions,
  facilitiesOptions,
  searchPlaceholder,
}: {
  filters: BookingRequestsFiltersState;
  onApply: (filter: BookingRequestsFiltersState) => void;
  userIsProvider: boolean;
  searchOptions: string[];
  facilitiesOptions: Record<string, unknown>[];
  searchPlaceholder: string;
}) => {
  const { reset, control, getValues, setValue } = useForm<BookingRequestsFiltersState>({
    defaultValues: defaultFilter,
  });

  useEffect(() => {
    if (filters) {
      reset(filters);
    }
  }, [filters, reset]);

  const clearFilters = () => {
    reset(defaultFilter);
    applyFilters();
  };

  const applyFilters = () => {
    if (onApply) {
      onApply(getValues());
    }
  };

  const onToggle = () => {
    if (filters) {
      reset(filters);
    }
  };

  const changeSortDirection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const sortDirection = getValues().sortDirection === 'asc' ? 'desc' : 'asc';
    setValue('sortDirection', sortDirection);
    applyFilters();
  };

  const getShiftDates = (range: [string, string]) => {
    const [from, to] = range;
    return {
      from,
      to,
    };
  };

  const isShowStatus = true;

  const sortOptions = useMemo(() => {
    return userIsProvider
      ? isShowStatus
        ? PROVIDER_SORT_OPTIONS()
        : PROVIDER_SORT_OPTIONS().filter((option) => option.value !== SortBy.STATUS)
      : HCO_SORT_OPTIONS();
  }, [userIsProvider, isShowStatus]);

  // TODO: should be encapsulated in a separate react hook to avoid code duplication
  const filtersFormValues = useWatch({
    control,
  });

  const filtersCount = useMemo(() => {
    const values = filtersFormValues;

    return Object.keys(values).filter((key) => {
      if (Array.isArray(values[key])) {
        return values[key].filter((i) => i).length > 0;
      }
    })?.length;
  }, [filtersFormValues]);

  return (
    <>
      <Row className="connection-requests-filters">
        <Col xs={12} lg={5} xl={4}>
          <AutoSuggest
            value={filters.search}
            placeholder={searchPlaceholder}
            options={searchOptions}
            onChange={(value) => {
              setValue('search', value);
              applyFilters();
            }}
          />
        </Col>
        <Col lg="auto" xs={12} className="p-lg-0 py-3">
          <FilterDropdown
            isDirty={!!filtersCount}
            onClear={clearFilters}
            onApply={applyFilters}
            onToggle={onToggle}
            filtersCount={filtersCount}
          >
            {!userIsProvider && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Specialties</Form.Label>
                <SelectController
                  control={control as unknown as Control<Record<string, unknown>>}
                  name="specialties"
                  placeholder=""
                  options={SPECIALTIES_OPTIONS}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold" htmlFor="healthcareFacility">
                Facilities
              </Form.Label>
              <SelectController
                control={control as unknown as Control<Record<string, unknown>>}
                name="healthcareFacility"
                placeholder=""
                options={
                  facilitiesOptions?.map((facility) => ({
                    value: facility.id,
                    label: facility.name,
                  })) || []
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="fw-bold">Shift Dates</Form.Label>
            </Form.Group>
            <DateRangePiker
              placeholder="mm/dd/yyyy"
              mask="__/__/____"
              dateFormat={TIME_FORMAT_OUTPUT}
              value={getShiftDates(getValues('shiftDate'))}
              onChange={(range) => {
                setValue('shiftDate', [range.from, range.to], { shouldDirty: true });
              }}
            />
          </FilterDropdown>
        </Col>
        <Col xs={12} lg={4}>
          <SelectController
            control={control as unknown as Control<Record<string, unknown>>}
            name="sortBy"
            placeholder="Sort by"
            isMulti={false}
            options={sortOptions}
            components={{
              Control: ({ children, ...props }: ControlProps) => (
                <components.Control {...props}>
                  <span className="sorting-btn">
                    {getValues().sortDirection === 'asc' ? (
                      <BsSortUp onMouseDown={changeSortDirection} size={16} />
                    ) : (
                      <BsSortDown onMouseDown={changeSortDirection} size={16} />
                    )}
                  </span>
                  {children}
                </components.Control>
              ),
            }}
            onChange={() => {
              applyFilters();
            }}
          />
        </Col>
      </Row>
    </>
  );
};
